export interface ConditionFAQ {
  q: string;
  a: string;
}

export interface Condition {
  slug: string;
  /** Short label used in navigation, cards and internal links */
  name: string;
  /** H1 of the page */
  title: string;
  /** <title> tag */
  metaTitle: string;
  metaDescription: string;
  /** Card teaser on the index page */
  summary: string;
  intro: string[];
  symptoms: string[];
  warningSigns: string[];
  consultation: string[];
  faqs: ConditionFAQ[];
}

export const conditions: Condition[] = [
  {
    slug: "anxiety-and-depression",
    name: "Anxiety & Depression",
    title: "Anxiety and Depression Support in Brazil",
    metaTitle: "Anxiety & Depression Support in Brazil | English-Speaking Doctor",
    metaDescription:
      "English-speaking family physician in Brazil for anxiety and depression. Online consultations nationwide.",
    summary:
      "Living abroad can amplify stress, isolation and low mood. Talk it through with a board-certified family physician in English.",
    intro: [
      "Moving to a new country, working far from home or travelling for long periods can put real pressure on your mental health. Language barriers, distance from family, a different pace of life and the loss of a familiar support network are common reasons expats and long-stay travellers in Brazil start feeling anxious, low or exhausted.",
      "Our board-certified family physicians provide an initial clinical evaluation in English, helps you understand what you are experiencing, discusses the options available in Brazil and refers you to a psychologist or psychiatrist when that is the right step.",
    ],
    symptoms: [
      "Persistent worry, restlessness or a sense of dread",
      "Difficulty falling asleep, staying asleep or waking unrefreshed",
      "Low mood, loss of interest in things you used to enjoy",
      "Fatigue, poor concentration or memory lapses",
      "Panic episodes with palpitations, chest tightness or shortness of breath",
      "Appetite changes, unexplained weight loss or gain",
      "Physical complaints without a clear cause: headaches, stomach pain, muscle tension",
    ],
    warningSigns: [
      "Thoughts of harming yourself or others",
      "Inability to carry out daily activities, work or self-care",
      "Sudden severe chest pain, fainting or neurological symptoms (these need emergency assessment)",
      "Heavy alcohol or substance use to cope",
    ],
    consultation: [
      "A structured conversation about your history, current symptoms, sleep, work and life context.",
      "Screening with validated clinical tools when appropriate.",
      "A clear explanation of what is likely going on, in plain English.",
      "A shared plan: lifestyle measures, follow-up, referral to English-speaking mental health professionals in Brazil, and medication only when clinically indicated after the evaluation.",
    ],
    faqs: [
      {
        q: "Can I be seen online for anxiety or depression in Brazil?",
        a: "Yes. Online consultations are conducted in compliance with CFM Resolution nº 2.314/2022 and are available throughout Brazil. If your situation needs an in-person assessment, the physician will tell you clearly.",
      },
      {
        q: "Will I automatically get a prescription?",
        a: "No. Medication is prescribed only when it is clinically indicated after a live medical evaluation. Many situations are managed with follow-up, lifestyle measures and referral to therapy.",
      },
      {
        q: "Is the consultation in English?",
        a: "Yes. Our physicians are fluent in English and native in Portuguese, so nothing gets lost in translation.",
      },
      {
        q: "Can I continue treatment I started in my home country?",
        a: "Bring your records and current treatment details to the consultation. The physician will review them and discuss how care can be continued safely in Brazil.",
      },
    ],
  },
  {
    slug: "travelers-diarrhea",
    name: "Traveler's Diarrhea",
    title: "Traveler's Diarrhea Treatment in Brazil",
    metaTitle: "Traveler's Diarrhea in Brazil | English-Speaking Doctor",
    metaDescription:
      "Traveler's diarrhea in Brazil? Online evaluation in English by a board-certified family physician.",
    summary:
      "Stomach upset after a change of food or water is the most common problem travellers face. Get assessed quickly, in English.",
    intro: [
      "Traveler's diarrhea is the most frequent health complaint among visitors to Brazil. It usually follows exposure to unfamiliar food, water, ice or street food and starts within the first days of the trip.",
      "Most episodes settle on their own, but dehydration can develop quickly in a hot climate, and some cases need treatment. A short online consultation can tell you which situation you are in and what to do next.",
    ],
    symptoms: [
      "Loose or watery stools, often three or more times a day",
      "Abdominal cramps and urgency",
      "Nausea, sometimes vomiting",
      "Low-grade fever",
      "Bloating and loss of appetite",
    ],
    warningSigns: [
      "Blood or mucus in the stool",
      "High fever above 38.5°C",
      "Signs of dehydration: very dark urine, dizziness on standing, dry mouth, no urine for many hours",
      "Severe or localised abdominal pain",
      "Symptoms lasting more than three days, or any diarrhea in an infant or elderly person",
    ],
    consultation: [
      "Review of what you ate, where you travelled and how the symptoms evolved.",
      "Assessment of hydration and of red flags that would need in-person care or tests.",
      "Practical rehydration and diet guidance you can start immediately.",
      "Laboratory tests or medication when clinically indicated after the evaluation.",
    ],
    faqs: [
      {
        q: "How fast can I be seen?",
        a: "Appointments are arranged by WhatsApp during service hours (daily, 07:00–22:00 BRT). Same-day appointments when availability allows.",
      },
      {
        q: "Do I need antibiotics?",
        a: "Most cases do not. Antibiotics are considered only when the clinical picture justifies them, and are prescribed only when clinically indicated after a live evaluation.",
      },
      {
        q: "Can you help from anywhere in Brazil?",
        a: "Yes. Online consultations cover the whole country, whether you are in Rio de Janeiro, São Paulo, the Northeast or the Amazon region.",
      },
    ],
  },
  {
    slug: "dengue-and-tropical-fevers",
    name: "Dengue & Tropical Fevers",
    title: "Dengue, Zika and Chikungunya Care in Brazil",
    metaTitle: "Dengue & Tropical Fever Care in Brazil | English-Speaking Doctor",
    metaDescription:
      "Fever in Brazil? Dengue, Zika and chikungunya assessed online in English by a board-certified family physician.",
    summary:
      "Mosquito-borne illnesses are common in Brazil. Know what your fever means and when you need urgent testing.",
    intro: [
      "Dengue, Zika and chikungunya are transmitted by the Aedes aegypti mosquito and circulate in most Brazilian states, with peaks during the hot and rainy months. For visitors who have never been exposed, a sudden fever can be alarming.",
      "The priority is to distinguish a self-limiting viral illness from dengue with warning signs, which requires prompt testing and monitoring. A clinical evaluation in English helps you act on time instead of guessing.",
    ],
    symptoms: [
      "Sudden high fever",
      "Severe headache and pain behind the eyes",
      "Intense muscle and joint pain",
      "Skin rash, often appearing after a few days",
      "Nausea, vomiting, marked fatigue",
    ],
    warningSigns: [
      "Persistent vomiting or inability to keep fluids down",
      "Severe abdominal pain",
      "Bleeding gums, nosebleeds, bruising or blood in vomit or stool",
      "Drowsiness, confusion or extreme weakness",
      "Fever that improves and then worsens again",
    ],
    consultation: [
      "Careful history of the fever, travel, mosquito exposure and symptom timeline.",
      "Guidance on which laboratory tests are appropriate and when to do them.",
      "Explanation of what to avoid — for example, certain painkillers are not recommended when dengue is suspected.",
      "Clear instructions on monitoring at home and on exactly when to seek emergency care.",
    ],
    faqs: [
      {
        q: "Can dengue be diagnosed online?",
        a: "The consultation assesses your risk and guides testing. Confirmation requires laboratory tests, and severe cases require in-person and sometimes hospital care. The physician will direct you accordingly.",
      },
      {
        q: "What should I do while waiting for the appointment?",
        a: "Rest, drink fluids and avoid self-medicating with anti-inflammatories. If you have any warning sign listed above, go to an emergency service or call 192 (SAMU).",
      },
      {
        q: "Is there a vaccine or prophylaxis?",
        a: "Vaccination policy in Brazil changes over time and depends on age and previous infection. This can be discussed during a consultation based on your individual situation.",
      },
    ],
  },
  {
    slug: "respiratory-infections-flu-covid",
    name: "Flu, COVID & Respiratory Infections",
    title: "Flu, COVID-19 and Respiratory Infections in Brazil",
    metaTitle: "Flu, COVID & Respiratory Infections in Brazil | English-Speaking Doctor",
    metaDescription:
      "Cough, sore throat or fever in Brazil? Online consultation in English with a board-certified family physician.",
    summary:
      "Colds, flu, COVID-19, sinus and throat infections — assessed in English, with clear guidance on tests and recovery.",
    intro: [
      "Respiratory infections spread easily during travel: long flights, crowded transport, air conditioning and a change of climate all contribute. Most are viral and improve on their own, but it is not always obvious when a cough deserves more attention.",
      "An evaluation in English clarifies what you likely have, whether testing is worthwhile, what you can safely take, and whether you are fit to fly or need to postpone travel.",
    ],
    symptoms: [
      "Sore throat, cough, hoarseness",
      "Nasal congestion or discharge, facial pressure",
      "Fever, chills, body aches",
      "Fatigue and headache",
      "Loss of smell or taste",
    ],
    warningSigns: [
      "Shortness of breath or difficulty speaking full sentences",
      "Chest pain",
      "Fever persisting beyond five days or returning after improvement",
      "Bluish lips, confusion or severe drowsiness",
      "Any breathing difficulty in a child or an older adult",
    ],
    consultation: [
      "History and symptom review, including underlying conditions and vaccination status.",
      "Guidance on testing (COVID-19, influenza) when it will change management.",
      "Symptom-relief advice and a realistic recovery timeline.",
      "A medical certificate or prescription when clinically indicated after the evaluation, and referral for in-person examination if your chest needs to be listened to.",
    ],
    faqs: [
      {
        q: "Can I get a medical certificate for work or my airline?",
        a: "A medical certificate can be issued during the consultation when it is clinically justified by the evaluation.",
      },
      {
        q: "Do I need antibiotics for a cough?",
        a: "Most respiratory infections are viral and antibiotics do not help. They are prescribed only when the clinical evaluation indicates a bacterial infection.",
      },
      {
        q: "Should I cancel my flight?",
        a: "That depends on your symptoms and any underlying conditions. It is one of the practical questions the consultation is designed to answer.",
      },
    ],
  },
  {
    slug: "skin-rashes-and-insect-bites",
    name: "Skin Rashes & Insect Bites",
    title: "Skin Rashes, Insect Bites and Sun Reactions in Brazil",
    metaTitle: "Skin Rashes & Insect Bites in Brazil | English-Speaking Doctor",
    metaDescription:
      "Rash or insect bites in Brazil? Online evaluation in English by a board-certified family physician.",
    summary:
      "Bites, heat rash, fungal infections and allergic reactions are common in a tropical climate — and easy to misidentify.",
    intro: [
      "Brazil's heat, humidity, beaches and vegetation bring skin problems that many visitors have never seen before: sandfly and mosquito bites, sand flea infestations, heat rash, fungal infections between the toes or in skin folds, and contact reactions to plants, sunscreen or jellyfish.",
      "Skin complaints are well suited to a video consultation, since a good history plus clear photos or live video usually allows an accurate assessment.",
    ],
    symptoms: [
      "Itchy red bumps, often grouped on exposed skin",
      "Spreading rash, blisters or peeling",
      "Ring-shaped or scaly patches, common in humid weather",
      "Painful, warm, swollen skin around a bite or scratch",
      "Hives after food, medication or sun exposure",
    ],
    warningSigns: [
      "Swelling of the lips, tongue or face, or difficulty breathing (emergency — call 192)",
      "Rapidly spreading redness with fever",
      "A bite or wound producing pus",
      "A dark, changing or bleeding skin lesion",
      "Rash with fever, which may indicate a systemic infection",
    ],
    consultation: [
      "Review of where you have been, what you were exposed to and how the rash evolved.",
      "Visual assessment by video or photographs you share before the appointment.",
      "Advice on skin care, itching relief and prevention of further bites.",
      "Topical or oral treatment when clinically indicated, or dermatology referral when a specialist is needed.",
    ],
    faqs: [
      {
        q: "Should I send photos?",
        a: "Yes. Clear, well-lit photographs taken before the consultation make the assessment considerably more accurate.",
      },
      {
        q: "Can a rash be diagnosed without an in-person exam?",
        a: "Many can. When touch or magnification is required, the physician will recommend an in-person visit or a dermatology referral.",
      },
      {
        q: "What about sand fleas (bicho-de-pé) or jellyfish stings?",
        a: "Both are common on Brazilian beaches and can be assessed in the consultation, with practical first-aid and treatment guidance.",
      },
    ],
  },
  {
    slug: "urinary-tract-infections",
    name: "Urinary Tract Infections",
    title: "Urinary Tract Infection (UTI) Care in Brazil",
    metaTitle: "UTI Treatment Guidance in Brazil | English-Speaking Doctor",
    metaDescription:
      "Painful urination while in Brazil? Online consultation in English with a board-certified family physician.",
    summary:
      "Burning, urgency and frequent urination are common while travelling — and need proper evaluation rather than guesswork.",
    intro: [
      "Urinary tract infections are frequent among travellers: dehydration, heat, long journeys and changes in routine all increase the risk. Symptoms are uncomfortable and tend to appear at inconvenient moments.",
      "A prompt evaluation in English clarifies whether this is a simple lower urinary tract infection, something that requires urine testing, or a kidney infection needing urgent in-person care.",
    ],
    symptoms: [
      "Burning or pain when passing urine",
      "Needing to urinate frequently or urgently",
      "Cloudy, strong-smelling or blood-tinged urine",
      "Lower abdominal discomfort",
      "Feeling unwell without a clear cause, particularly in older adults",
    ],
    warningSigns: [
      "Fever with chills",
      "Back or flank pain",
      "Nausea and vomiting",
      "Symptoms during pregnancy",
      "Symptoms in a man, a child, or anyone with recurrent infections",
    ],
    consultation: [
      "Detailed symptom history and assessment of risk factors and previous episodes.",
      "Guidance on urine testing, and where to have it done near you in Brazil.",
      "Advice on hydration and symptom relief while results are pending.",
      "Treatment when clinically indicated after the evaluation, with referral for in-person care if a kidney infection is suspected.",
    ],
    faqs: [
      {
        q: "Can a UTI be handled online?",
        a: "Uncomplicated cases in otherwise healthy adults often can. Complicated cases, pregnancy or suspected kidney involvement require in-person assessment, and the physician will tell you clearly which applies.",
      },
      {
        q: "Where do I get a urine test in Brazil?",
        a: "Private laboratories are widely available and generally fast. The consultation includes guidance on what to request and how to proceed.",
      },
      {
        q: "Can I get a receipt for my travel insurance?",
        a: "Yes. Detailed medical receipts for international insurance claims are provided.",
      },
    ],
  },
  {
    slug: "prescription-refills-chronic-conditions",
    name: "Chronic Conditions & Continuity of Care",
    title: "Chronic Conditions and Continuity of Care in Brazil",
    metaTitle: "Chronic Condition Care in Brazil | English-Speaking Family Doctor",
    metaDescription:
      "Chronic condition while in Brazil? English-speaking board-certified family physician online nationwide.",
    summary:
      "Hypertension, diabetes, thyroid disease, asthma and other ongoing conditions followed properly while you live in Brazil.",
    intro: [
      "If you are living in Brazil for months or years, an ongoing condition needs a doctor who knows your history, monitors your results and adjusts treatment over time. Family and community medicine exists precisely for this kind of continuous care.",
      "Our physician reviews your records from your home country, discusses the medications available in Brazil (names and formulations often differ) and establishes a follow-up plan so your treatment is never improvised.",
    ],
    symptoms: [
      "Hypertension and cardiovascular risk",
      "Type 2 diabetes and pre-diabetes",
      "Thyroid disorders",
      "Asthma, allergic rhinitis and other chronic respiratory conditions",
      "High cholesterol and metabolic follow-up",
      "General preventive care and routine check-ups",
    ],
    warningSigns: [
      "Chest pain, severe shortness of breath or neurological symptoms — seek emergency care (192)",
      "Blood pressure or blood glucose readings far outside your usual range",
      "Running out of essential medication with no medical follow-up in place",
      "New symptoms that appeared after a change of treatment",
    ],
    consultation: [
      "Review of your medical history, records and current medications.",
      "Explanation of the Brazilian equivalents of your usual medications and how pharmacies work here.",
      "Laboratory monitoring plan and interpretation of results.",
      "Continuity: scheduled follow-up consultations, with prescriptions issued only when clinically indicated after a live evaluation.",
    ],
    faqs: [
      {
        q: "Can you simply refill a medication I already take?",
        a: "Any prescription follows a clinical evaluation. Bring your records — in many cases continuity of an established treatment is entirely appropriate, but that judgment is made during the consultation.",
      },
      {
        q: "Is my home-country prescription valid in Brazil?",
        a: "Brazilian pharmacies generally require a prescription issued by a physician registered in Brazil. Documents issued by Rio Med Care physicians are valid nationwide.",
      },
      {
        q: "Do you sell or deliver medication?",
        a: "No. Rio Med Care provides medical consultations only. Medication is purchased at any licensed pharmacy of your choice.",
      },
    ],
  },
  {
    slug: "childrens-health-pediatrics",
    name: "Children's Health",
    title: "Children's Health for Expat and Travelling Families in Brazil",
    metaTitle: "English-Speaking Pediatric Care in Brazil | Rio Med Care",
    metaDescription:
      "Online pediatric consultations in English throughout Brazil, with a board-certified family physician.",
    summary:
      "Fevers, rashes, ear pain and stomach bugs in children — assessed in English, with clear advice for worried parents.",
    intro: [
      "A child falling ill abroad is stressful for any family, especially when you are unsure how the local health system works or which symptoms justify going to a hospital.",
      "Our physicians see children of all ages online and explains, in English, what is happening, what you can do at home and exactly which signs mean you should seek in-person or emergency care.",
    ],
    symptoms: [
      "Fever with or without a clear source",
      "Cough, blocked nose, sore throat, ear pain",
      "Vomiting, diarrhea and concerns about dehydration",
      "Rashes and insect bites",
      "Feeding, sleep and behaviour concerns",
    ],
    warningSigns: [
      "Difficulty breathing, rapid breathing or grunting",
      "A drowsy child who is difficult to wake, or is unusually irritable",
      "Fever in an infant under three months",
      "Signs of dehydration: no wet nappies, no tears, sunken eyes",
      "A rash that does not fade when pressed, or a seizure — emergency care (192)",
    ],
    consultation: [
      "Full history including birth, vaccinations, growth and current symptoms.",
      "Video assessment of the child's general condition, breathing and hydration.",
      "Home-care guidance with clear, specific criteria for seeking in-person care.",
      "Prescriptions or referral to a pediatric specialist when clinically indicated after the evaluation.",
    ],
    faqs: [
      {
        q: "Is the online pediatric consultation appropriate for every age?",
        a: "Children of all ages can be assessed, but some situations — especially very young infants and breathing difficulty — require in-person examination. The physician will say so immediately when that is the case.",
      },
      {
        q: "Can you advise on vaccinations in Brazil?",
        a: "Yes. The Brazilian immunisation schedule can be compared with your home-country schedule during the consultation.",
      },
      {
        q: "What does an online pediatric consultation cost?",
        a: "US$ 50, the same as an adult online consultation. Payment is by credit or debit card.",
      },
    ],
  },
  {
    slug: "sunburn-heat-illness-and-dehydration",
    name: "Sunburn, Heat Illness & Dehydration",
    title: "Sunburn, Heat Illness and Dehydration in Brazil",
    metaTitle: "Sunburn & Heat Illness Care in Brazil | English-Speaking Doctor",
    metaDescription:
      "Sunburn, heat exhaustion or dehydration in Brazil? Online consultation in English with a board-certified family physician.",
    summary:
      "Rio's sun is stronger than most visitors expect. Burns, heat exhaustion and dehydration are frequent and avoidable.",
    intro: [
      "Ultraviolet levels in Brazil are high throughout the year, and visitors regularly underestimate them during a first day at the beach. Combined with heat and humidity, this leads to severe sunburn, heat exhaustion and dehydration.",
      "A consultation helps you manage the burn or heat illness properly, avoid complications such as infection, and recognise the point at which the situation becomes a medical emergency.",
    ],
    symptoms: [
      "Red, painful, hot skin with or without blistering",
      "Headache, dizziness, nausea after sun exposure",
      "Heavy sweating followed by weakness or muscle cramps",
      "Dark urine, intense thirst, dry mouth",
      "Chills or feeling feverish after a long day outdoors",
    ],
    warningSigns: [
      "Confusion, fainting or seizures",
      "Hot dry skin with the absence of sweating and a very high temperature — suspected heat stroke, an emergency (192)",
      "Extensive blistering, or blistering over the face",
      "Signs of skin infection: increasing pain, pus, spreading redness",
      "No urine output for many hours",
    ],
    consultation: [
      "Assessment of the burn's extent and depth, and of your hydration status.",
      "Practical wound care, pain relief and rehydration guidance.",
      "Advice on realistic sun protection for the Brazilian climate.",
      "Medication or in-person care when clinically indicated after the evaluation.",
    ],
    faqs: [
      {
        q: "When does sunburn need a doctor?",
        a: "When there is extensive blistering, fever, confusion, or signs of infection. A consultation can also simply confirm that home care is sufficient.",
      },
      {
        q: "Is heat exhaustion serious?",
        a: "It can progress to heat stroke, which is a medical emergency. Early recognition and cooling matter, and the consultation covers exactly what to watch for.",
      },
      {
        q: "Can I be seen from the beach city I am visiting?",
        a: "Yes. Online consultations cover all of Brazil.",
      },
    ],
  },
  {
    slug: "food-poisoning-and-stomach-pain",
    name: "Food Poisoning & Stomach Pain",
    title: "Food Poisoning, Nausea and Stomach Pain in Brazil",
    metaTitle: "Food Poisoning & Stomach Pain in Brazil | English-Speaking Doctor",
    metaDescription:
      "Nausea, vomiting or abdominal pain in Brazil? Online evaluation in English by a board-certified family physician.",
    summary:
      "Suspected food poisoning, reflux, motion sickness or abdominal pain evaluated quickly and clearly, in English.",
    intro: [
      "Street food, seafood, long bus journeys, boat trips and a change of diet all make digestive complaints common in Brazil. Most resolve within a day or two, but abdominal pain has many causes and some of them are serious.",
      "The purpose of the consultation is to separate a self-limiting upset from a problem that needs tests, in-person examination or urgent care — and to give you a plan you can follow right away.",
    ],
    symptoms: [
      "Nausea and vomiting, often shortly after a meal",
      "Cramping abdominal pain",
      "Diarrhea, bloating and gas",
      "Heartburn or a burning sensation behind the breastbone",
      "Motion sickness on boats, buses or winding mountain roads",
    ],
    warningSigns: [
      "Severe pain focused in one area, particularly the lower right abdomen",
      "Persistent vomiting with inability to keep fluids down",
      "Vomiting blood, or black tarry stools",
      "High fever with abdominal pain",
      "Pain that worsens steadily over hours — seek emergency care (192)",
    ],
    consultation: [
      "History of what you ate, the timing of symptoms and how the pain behaves.",
      "Assessment of hydration and screening for surgical warning signs.",
      "Diet, rehydration and symptom-relief guidance.",
      "Tests, medication, or in-person or emergency referral when clinically indicated after the evaluation.",
    ],
    faqs: [
      {
        q: "How long should food poisoning last?",
        a: "Most cases improve within 24 to 72 hours. Longer episodes, or any warning sign above, should be evaluated.",
      },
      {
        q: "Can abdominal pain really be assessed by video?",
        a: "The consultation identifies whether your pain is low risk or needs hands-on examination. When examination is required, the physician will direct you to in-person or emergency care.",
      },
      {
        q: "Can I get a medical certificate if I miss work?",
        a: "Yes, when it is clinically justified by the evaluation.",
      },
    ],
  },
  {
    slug: "swimmers-ear-and-ear-infections",
    name: "Swimmer's Ear & Ear Infections",
    title: "Swimmer's Ear and Ear Infection Care in Brazil",
    metaTitle: "Swimmer's Ear & Ear Infections in Brazil | English-Speaking Doctor",
    metaDescription:
      "Ear pain after the beach or pool in Brazil? Online evaluation in English by a board-certified physician.",
    summary:
      "Ear pain, itching or blocked hearing after swimming in the sea or pool are common — get assessed in English before symptoms worsen.",
    intro: [
      "Frequent time in the ocean, hotel pools and humid air make ear canal infections, often called swimmer's ear, a common problem for visitors to Brazil. Trapped moisture can irritate the ear canal and allow an infection to develop, sometimes with pain that worsens quickly.",
      "Middle ear infections, often following a cold, flight or sinus congestion, are a different problem. An online consultation in English can assess the symptoms and guide the next step, although looking inside the ear with an otoscope may require an in-person examination.",
    ],
    symptoms: [
      "Ear pain that worsens when touching or pulling the outer ear",
      "Itching or a feeling of fullness inside the ear canal",
      "Clear, yellowish or foul-smelling discharge",
      "Temporary muffled hearing",
      "Redness or swelling of the ear canal or outer ear",
      "Ear pain or pressure after a cold or flight",
    ],
    warningSigns: [
      "Fever with severe ear pain, especially in a child",
      "Swelling, redness or pain behind or around the ear",
      "Sudden hearing loss, severe dizziness or persistent ringing",
      "Facial weakness or drooping",
      "Severe pain, rapidly worsening symptoms or discharge with blood",
    ],
    consultation: [
      "Review of recent swimming, flights, colds and how the pain or discharge developed.",
      "Visual assessment by video and photographs when useful, with clear advice when an otoscope examination is needed.",
      "Practical guidance on keeping the ear dry and reducing discomfort safely.",
      "Treatment when clinically indicated after the evaluation, or referral for an in-person or ENT assessment when the ear canal or eardrum must be examined directly.",
    ],
    faqs: [
      {
        q: "Can an ear infection be assessed online?",
        a: "A detailed history and external examination by video can guide the initial assessment, but they do not replace an otoscope examination. The physician will tell you clearly if an in-person visit is needed.",
      },
      {
        q: "How can I reduce the risk after swimming?",
        a: "Dry the outer ears gently, tilt your head to let water drain and avoid putting cotton swabs or other objects inside the ear canal.",
      },
      {
        q: "Can I keep swimming while my ear heals?",
        a: "It is usually best to keep the ear dry until symptoms have resolved. Specific advice depends on the cause and severity found during the evaluation.",
      },
      {
        q: "Are online consultations compliant with Brazilian medical rules?",
        a: "Yes. Online consultations are conducted in compliance with CFM Resolution nº 2.314/2022. In-person care is recommended whenever a physical examination is clinically necessary.",
      },
    ],
  },
  {
    slug: "minor-injuries-and-beach-accidents",
    name: "Minor Injuries & Beach Accidents",
    title: "Minor Injuries and Beach Accident Care in Brazil",
    metaTitle: "Minor Injuries & Beach Accidents in Brazil | English-Speaking Doctor",
    metaDescription:
      "Cuts, sprains or a fall on Rio's beaches? Medical evaluation in English, wound-care guidance and clear advice on when to seek in-person care.",
    summary:
      "Cuts, sprains, stings and small falls can disrupt a holiday. Get a clear assessment and practical care guidance in English.",
    intro: [
      "Beach days, cobblestones, watersports and unfamiliar terrain can lead to cuts, scrapes, sprains, falls and marine stings. Many injuries do not need an emergency department, but they still need proper cleaning and an assessment of whether stitches, imaging or a tetanus review may be necessary.",
      "An online consultation in English can help determine the appropriate level of care. Procedures such as suturing and fracture treatment require an appropriate medical facility, and the physician will tell you where to go.",
    ],
    symptoms: [
      "Cuts, scrapes or puncture wounds from rocks, shells or glass",
      "Ankle, wrist or knee pain and swelling after a twist or fall",
      "Bruising, localised swelling or difficulty bearing weight",
      "Minor burns or blisters after outdoor activity",
      "Jellyfish or sea urchin stings with local pain, welts or embedded spines",
    ],
    warningSigns: [
      "Deep wounds, uncontrolled bleeding or visible fat, muscle or bone",
      "Inability to bear weight, obvious deformity or a suspected fracture",
      "Head injury with loss of consciousness, confusion, repeated vomiting or a worsening headache",
      "Spreading redness, pus, fever or increasing pain around a wound",
      "Difficulty breathing, facial swelling or widespread hives after a sting",
      "Any serious injury or rapidly worsening condition — call 192 (SAMU) or go to an emergency department",
    ],
    consultation: [
      "Assessment of how the injury happened and its severity, using video and photographs when appropriate.",
      "Guidance on wound cleaning, dressing, swelling control and safe symptom relief.",
      "A clear recommendation on whether conservative care is suitable or an in-person examination, X-ray, suturing or emergency treatment is needed.",
      "Medical documentation for travel insurance claims when applicable.",
    ],
    faqs: [
      {
        q: "Can a cut be stitched during an online consultation?",
        a: "No. Suturing and fracture care require an appropriate clinical setting. The physician can assess the injury, recommend where to go and support suitable wound care afterward.",
      },
      {
        q: "I twisted my ankle — do I need an X-ray?",
        a: "Not every ankle injury needs imaging. The consultation assesses signs that suggest a fracture and explains where to obtain an X-ray when necessary.",
      },
      {
        q: "What should I do after a jellyfish sting?",
        a: "Leave the water, avoid rubbing the area and seek advice because first aid can vary by species. Breathing difficulty, facial swelling or widespread symptoms require immediate emergency care.",
      },
      {
        q: "Is this service a substitute for an emergency department?",
        a: "No. Heavy bleeding, suspected fractures, serious head injuries and severe allergic reactions need immediate emergency care. This service assesses and supports minor injuries only.",
      },
    ],
  },
  {
    slug: "sexual-health-and-sti-testing",
    name: "Sexual Health & STI Testing",
    title: "Sexual Health and STI Testing in Brazil",
    metaTitle: "Sexual Health & STI Testing in Brazil | English-Speaking Doctor",
    metaDescription:
      "Confidential sexual health consultation and STI testing guidance in English, with a board-certified physician online throughout Brazil and in Rio.",
    summary:
      "Confidential, respectful guidance on sexual health concerns, testing and prevention in English from a board-certified physician.",
    intro: [
      "A possible exposure, new symptoms or a routine sexual health check are common reasons to seek medical care while abroad. Language barriers and uncertainty about local services can make people delay an assessment they would normally arrange without hesitation.",
      "Our physicians provide a confidential, respectful consultation in English to discuss symptoms, testing options and prevention. They can guide you to an appropriate laboratory or specialist in Brazil and explain when a physical examination or time-sensitive in-person care is needed.",
    ],
    symptoms: [
      "Unusual discharge, odour, itching or discomfort",
      "Genital sores, warts, rashes or unusual lumps",
      "Pain during urination or sexual activity",
      "Pelvic or lower abdominal pain",
      "No symptoms, but concern after a recent exposure or before or after a new relationship",
    ],
    warningSigns: [
      "Severe pelvic or abdominal pain, especially with fever",
      "Heavy or unusual bleeding",
      "Widespread rash, joint pain or fever alongside genital symptoms",
      "A possible recent exposure to HIV: post-exposure prophylaxis is time-sensitive and should be assessed in person as soon as possible, ideally within hours and no later than 72 hours",
      "Sexual assault or immediate safety concerns — seek urgent in-person medical care and support",
    ],
    consultation: [
      "A private conversation about your history, symptoms and concerns without judgment.",
      "Guidance on which tests are appropriate and where to have them performed in Brazil.",
      "Explanation of results and next steps once testing is complete.",
      "Discussion of prevention, vaccination and PrEP or PEP assessment, with urgent referral when timing matters.",
      "Treatment when clinically indicated after the evaluation, or referral for an in-person examination when required.",
    ],
    faqs: [
      {
        q: "Is the consultation confidential?",
        a: "Yes. Consultations follow medical confidentiality and Brazilian data-protection requirements. Information is not shared with hotels, employers or travel companions without your permission, except where legally required.",
      },
      {
        q: "Can STIs be diagnosed or treated online?",
        a: "The consultation can assess risk, guide testing and explain results. Some symptoms, tests or procedures require an in-person examination, and the physician will make that clear when applicable.",
      },
      {
        q: "What should I do after a possible recent exposure to HIV?",
        a: "PEP must be started as soon as possible and no later than 72 hours after exposure. Seek urgent in-person evaluation immediately rather than waiting for a scheduled telemedicine appointment.",
      },
      {
        q: "Where can I get tested in Brazil?",
        a: "Private laboratories are widely available. The consultation includes guidance on the appropriate tests, timing and nearby options, followed by interpretation of your results.",
      },
    ],
  },
];

export const getCondition = (slug?: string) =>
  conditions.find((condition) => condition.slug === slug);
